/**
 * canOpenAddContent — shared pre-flight guard for every add-content entry point.
 *
 * Web port of the upload-in-progress check Flutter runs as the first thing inside
 * `NavigationService.navigateToAddContentScreen`
 * (`lib/services/navigation_service.dart:420-435`): when a video upload is already
 * running, show the "Upload in progress" display-info sheet and return WITHOUT
 * opening the add-content flow. Because Flutter's guard lives in the shared
 * navigation helper, it applies to every call site of that helper — so on web it
 * is called from every entry point that opens `AddContentSheet` on a
 * `navigateToAddContentScreen` path (profile upload, chat composer, community
 * composer, edit-profile avatar, comment video reply).
 *
 * Flutter returns `null` from the guarded branch, so a caller awaiting
 * `bool uploaded` (`comments_bottom_sheet.dart:915`) treats it as "not uploaded"
 * and creates nothing. The web equivalent is this `false` return: callers bail
 * out of their whole flow when it is `false`.
 *
 * Notes on the port:
 * - Flutter compares `videoUploadProgressNotifier.progress != 0`. The web slice
 *   tracks `isUploading` explicitly alongside `progress`, so the predicate here is
 *   the boolean `selectIsUploading(...)` — same condition, no magic-number
 *   comparison needed.
 * - This is a plain, hook-free helper (no React hooks), mirroring the established
 *   sibling `presentation/feature/liveStreaming/utils/launchBroadcasterGoLive.ts`:
 *   the caller — a page/sheet/hook that already has `useLocale()` — passes its
 *   resolved `l10n` and `screenKey`. Upload state is read straight off the Redux
 *   store singleton (`store.getState()`), which is what makes the helper callable
 *   from any handler without adding a subscription.
 * - The sibling `RemoteConfigService.budgetExceeded` guard that sits immediately
 *   after this one in Flutter (`navigation_service.dart:437-442`) is deliberately
 *   NOT ported here: web has no live `RemoteConfigService`, and the deferral is
 *   already recorded by the existing marker in
 *   `presentation/constants/remoteConfigConstants.ts:44-51` ("Start 'Add Content'
 *   flow (Flutter: `navigation_service.dart:437`)").
 *
 * Deliberately NOT wired: the in-app feedback screenshot picker
 * (`FeedbackPage.tsx`). Its Flutter counterpart (`in_app_feedback_screen.dart:73-81`)
 * picks through `AppFileService.getLocalImageMedia(...)` and never routes through
 * `navigateToAddContentScreen`, so this guard never runs for it on Flutter either.
 * Web only reaches `AddContentSheet` there because one shared sheet backs every
 * picker.
 */
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { store } from '@/presentation/store';
import { selectIsUploading } from '@/presentation/store/slices/uploadGlobalOverlayProgress/uploadGlobalOverlayProgressSlice';
import type { l10n } from '@/presentation/locale/l10n';

export interface CanOpenAddContentArgs {
  /** Resolved localizations from the caller (no hooks in this plain helper). */
  l10n: l10n;
  /** Screen key of the route/sheet opening the add-content flow. */
  screenKey?: string;
}

/**
 * Whether an add-content flow may be opened right now.
 *
 * @returns `false` — after firing the "Upload in progress" info sheet — while a
 *   video upload is running; `true` (silently, no sheet) otherwise. Callers must
 *   `return` on `false` and open nothing.
 */
export function canOpenAddContent({ l10n, screenKey }: CanOpenAddContentArgs): boolean {
  if (!selectIsUploading(store.getState())) {
    return true;
  }

  // Fire-and-forget, exactly as Flutter's `showDisplayInfoSheet(...)` is not
  // awaited before `return` (`navigation_service.dart:424-432`).
  void InfoSheet.show({
    title: l10n.publish.uploadInProgressTitle,
    infoText: l10n.publish.uploadInProgressDescription,
    okText: l10n.common.close,
    screenKey,
    // Stable per-call QA locator for THIS guard's sheet (the shared `sheet-info`
    // wrapper id is not unique per call). Test-only, l10n-exempt.
    qaId: 'upload-in-progress-sheet',
  });
  return false;
}
